// Chakra imports
import {
  Badge,
  Box,
  Flex,
  Grid,
  Select,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import LiftedTitleCard from "./LiftedTitleCard";
import React, { ReactNode } from "react";

interface MiniStatisticsProps {
  title: string;
  amount: string;
  percentage: number;
  chart?: ReactNode;
  dateRange?: string;
  amountColor?: string;
  backgroundGradient?: string;
  timeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
  tooltip?: string;
}

const MiniStatistics: React.FC<MiniStatisticsProps> = ({ 
  title, 
  amount, 
  percentage, 
  chart,
  dateRange = "since all time",
  amountColor,
  backgroundGradient,
  timeframe = "all-time",
  onTimeframeChange,
  tooltip
}) => {
  const defaultTextColor = useColorModeValue("gray.700", "white");
  // Use white text when gradient background is applied, otherwise use the amount color or default
  const textColor = backgroundGradient ? "white" : (amountColor || defaultTextColor);
  const innerCardBg = useColorModeValue("gray.200", "gray.700");

  return (
    <LiftedTitleCard title={title} tooltip={tooltip}>
      {/* Inner Card - smaller dark grey card with metric and graph */}
    <Card 
      overflow="hidden" 
      p="0" 
      w="100%"
      position="relative"
      display="flex"
      flexDirection="column"
        m="0"
        flex="1"
        minH="0"
        bg={innerCardBg}
        borderTopRadius="0"
        borderBottomRadius="md"
        border="none"
        boxShadow="0 -2px 8px rgba(0, 0, 0, 0.1)"
      {...(backgroundGradient && { 
        style: { 
          background: backgroundGradient,
        } 
      })}
    >
      {/* Grid pattern overlay */}
      {backgroundGradient && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `}
          backgroundSize="40px 40px"
          pointerEvents="none"
          zIndex={1}
        />
      )}
      <CardBody 
          p="0"
        overflow="hidden" 
        w="100%"
        m="0"
      >
        <Grid
            templateRows="auto auto"
          gap={{ base: '1px', sm: '2px', md: '3px' }}
          w="100%"
          m="0"
          minW="0"
        >
            {/* Row 1: Stat Number */}
          <Text
            fontSize={{ base: '2xl', sm: '3xl', md: '4xl' }} 
            color={textColor}
            lineHeight='1.1'
            fontWeight='medium'
            m='0'
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
              pt={{ base: '8px', sm: '10px', md: '12px' }}
            px={{ base: '6px', sm: '8px', md: '10px' }}
              fontFamily="'IBM Plex Sans', sans-serif"
          >
            {amount}
          </Text>

          {/* Row 2: Chart */}
          {chart && (
            <Box 
              w="100%" 
              h={{ base: '30px', sm: '38px', md: '45px' }}
              overflow="hidden"
              position="relative"
              m="0"
              minW="0"
              maxW="100%"
            >
              {chart}
            </Box>
          )}

        </Grid>
      </CardBody>
      
      {/* Undercard Section */}
      <Box
        w="100%"
        bg="transparent"
        borderTopRadius="0"
        borderBottomRadius={{ base: '15px', sm: '15px', md: '15px' }}
          pl={{ base: '3px', sm: '4px', md: '5px' }}
          pr={{ base: '8px', sm: '10px', md: '12px' }}
        py={{ base: '3px', sm: '4px', md: '5px' }}
        mt="auto"
      >
        <Flex
          justify="space-between"
          align="center"
          gap={{ base: '4px', sm: '6px', md: '8px' }}
        >
          <Badge
            bg="transparent"
            color={backgroundGradient ? "white" : (amountColor || defaultTextColor)}
              fontSize={{ base: '10px', sm: '11px', md: '12px' }}
            fontWeight="medium"
              px={{ base: '6px', sm: '7px', md: '8px' }}
              py={{ base: '1px', sm: '2px', md: '2px' }}
            borderRadius="full"
            border="1px solid"
            borderColor={backgroundGradient ? "white" : (amountColor || defaultTextColor)}
              fontFamily="'IBM Plex Sans', sans-serif"
          >
            {percentage >= 0 ? "+" : ""}{percentage}%
          </Badge>
          {onTimeframeChange && (
            <Select
              value={timeframe}
              onChange={(e) => onTimeframeChange(e.target.value)}
              size="xs"
              bg="transparent"
              color={backgroundGradient ? "white" : defaultTextColor}
              border="none"
              fontSize={{ base: '10px', sm: '11px', md: '12px' }}
              fontWeight="bold"
              w={{ base: '70px', sm: '80px', md: '90px' }}
              _hover={{ bg: "transparent" }}
              _focus={{ bg: "transparent", boxShadow: "none" }}
              sx={{
                option: {
                  bg: "gray.800",
                  color: "white",
                },
              }}
            >
              <option value="today">Today</option>
              <option value="7-days">7 days</option>
              <option value="14-days">14 days</option>
              <option value="30-days">30 days</option>
              <option value="all-time">All time</option>
            </Select>
          )}
        </Flex>
      </Box>
    </Card>
    </LiftedTitleCard>
  );
};

export default MiniStatistics;

